import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import type { LucideIcon } from "lucide-react";
import { type ComponentPropsWithoutRef, forwardRef } from "react";

interface RowActionButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  /** Popis akce — zobrazí se v tooltipu po najetí a jako aria-label. */
  label: string;
  icon: LucideIcon;
  testId: string;
  /** Destruktivní akce (archivace, smazání) — červený hover. */
  destructive?: boolean | undefined;
  /** Třídy jen pro ikonu (např. rotace chevronu) — tlačítko drží velikost. */
  iconClassName?: string | undefined;
}

/**
 * Jednotná akční ikona v řádku tabulky — jen ikona, popis se ukáže
 * v tooltipu po najetí (a čtečkám přes aria-label). Žádné textové popisky
 * vedle ikon. Forwarduje ref i HTML props, takže funguje i jako Radix
 * `asChild` trigger (AlertDialogTrigger, SheetTrigger…).
 */
export const RowActionButton = forwardRef<HTMLButtonElement, RowActionButtonProps>(
  function RowActionButton(
    { label, icon: Icon, testId, destructive = false, iconClassName, className, ...rest },
    ref,
  ) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              size="sm"
              variant="ghost"
              // `className` z props se MERGUJE za základ — spread `...rest`
              // dřív základní `h-8 w-8 p-0` přepisoval a tlačítko měnilo velikost.
              className={cn(
                "h-8 w-8 p-0",
                destructive && "text-destructive hover:text-destructive",
                className,
              )}
              aria-label={label}
              data-testid={testId}
              {...rest}
            >
              <Icon className={cn("h-4 w-4", iconClassName)} aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
